import angular from "angular";

import { Dates, PaymentChannel, SalaryDateLogic, Translations } from "@salaxy/core";
import AirDatepicker, { AirDatepickerViews } from "air-datepicker";
import localeEn from "air-datepicker/locale/en";
import localeFi from "air-datepicker/locale/fi";
import localeSv from "air-datepicker/locale/sv";
import { SessionService } from "../../services";
import { createPopper } from "@popperjs/core";

/**
 * Controller for datepicker control.
 */
export class DatepickerController implements angular.IController {
  public static $inject = ["SessionService", "$element", "$timeout", "$scope"];
  public form: angular.IFormController;

  /** Label for the control */
  public label: string;

  /** The model that is bound to the input */
  public model: angular.INgModelController;

  /** Form control name */
  public name: string;

  /** Validation error text */
  public validationError: string;

  /** The datepicker element instance. */
  private datepicker: any;

  /** True = can change multiple dates, false = single date selection. */
  public isMultiple: boolean;

  /** Selection mechanism and format: "month" | "year" | undefined (= dates) */
  public format: string;

  /** Mechanism to disable certain dates: "salary" and "next-weekday" are pre-set functions. If set as "disable-function", a custom disabling function can and must be given. */
  public dateDisabled: "salary" | "next-weekday" | "disable-function" | null;

  /** If dateDisabled="disable-function", this function acts as the disabler. */
  public disableFunction: any;

  /** Dates before this are disabled in the picker. */
  public minDate: string;

  /** Dates after this are disabled in the picker. */
  public maxDate: string;

  /** Defines HTML for tooltip button */
  public tooltipHtml: string;

  /** Payment channel. This affects which dates are disabled in the salary date selection. */
  public paymentChannel: PaymentChannel;

  /** Boolean indicating if the net salary payment to the worker is zero. */
  public zeroPayment: boolean;

  /**
   * If true, displays the control as read-only div instead of the input control.
   * This "read-only" attribute is visualized in a different way than standard html "readonly" (ng-readonly):
   * The input is not shown and display is more compact.
   * Also note, that you may change the same read-only for the form using readonly / ng-readonly attribute.
   * @example
   * ```html
   * <form name="foo" ng-readonly="true">
   * <!-- .... or plain HTML ... -->
   * <form name="foo" readonly="true">
   * <!-- .... or plain HTML ... -->
   * <salaxy-input name="input1" read-only="true"></salaxy-input>
   */
  public readOnly: boolean;

  /**
   * @deprecated Use "require" instead:
   * This attribute overlaps with required/ng-required directive, which produces unexpected results.
   */
  public required: string;

  /** In multiple mode, the days count is set in this variable. */
  public daysCount: number;

  /** True = show timepicker alongside the calendar. */
  public timepicker: boolean = false;

  /** False = hide Today/Clear buttons below the datepicker. Note: in multiple mode buttons are always hidden. */
  public showButtons: boolean = true;

  /** Datepicker is initialized only once. */
  private datepickerInitialized: boolean = false;

  /** Value of the datepicker. */
  private _value: any;

  /** Manual input value of the datepicker. */
  private _manualInput: any;

  /** True = inline datepicker. */
  private isInline: boolean = false;

  /** Getter and setter for the datepicker value. */
  public get datepickerValue() {
    if (this._manualInput != null) {
      return this._manualInput;
    }

    if (!this._value) return null;

    if (this.isInline) {
      return this._value;
    }

    // Format the displayed value based on the format setting.
    const jsDate = Dates.asJSDate(this._value);
    if (this.format == "month") {
      const month = jsDate.getMonth() + 1;
      const year = jsDate.getFullYear();
      return `${month}/${year}`;
    } else if (this.format == "year") {
      return jsDate.getFullYear().toString();
    }

    return Dates.getFormattedDate(jsDate);
  }
  public set datepickerValue(value: string) {
    this._manualInput = value;
  }

  /** External value that causes the disabled dates function to re-run and update the datepicker component. */
  public updateDisabledDates: any;

  constructor(private sessionService: SessionService, private $element: JQLite, private $timeout: angular.ITimeoutService, private $scope: angular.IScope) {
  }

  /** Set the values and defaults on init */
  public $onInit() {
    if (this.$element[0].tagName.toLowerCase() == "salaxy-datepicker") {
      this.isInline = true;
    }

    this.model.$render = () => {
      if (this.model.$viewValue) {
        // Special case: Taxcards view sets a hardcoded text value of 'today'.
        this._value = this.model.$viewValue === "today" ? Dates.getToday() : this.model.$viewValue;
      }
    };

    // Initialize inline datepicker after DOM is ready.
    if (this.isInline) {
      this.$timeout(() => {
        this.$timeout(() => {
          this.initDatepicker();
        });
      });
    }

    if (this.name && !this.label) {
      this.label = this.name;
    }

    // External attributes command the disabled dates in the calendar to update.
    this.$scope.$watch(() => this.updateDisabledDates, (newVal, oldVal) => {
      if (!this.datepicker) {
        return;
      }
      if (newVal !== oldVal) {
        this.onDisabledDatesExternalChange();
      }
    });

    // For inline datepicker, we need to watch the model value changes and update the min-max range and days count.
    this.$scope.$watch(() => this.model.$modelValue, (newVal, oldVal) => {
      if (!this.isInline || !this.datepicker) {
        return;
      }
      if (this.datepicker && (newVal.end !== oldVal.end || newVal.start !== oldVal.start)) {
        this.setDatepickerCalendarValue(newVal);
        if (this.minDate || this.maxDate) {
          this.datepicker.update({ minDate: this.minDate, maxDate: this.maxDate }, { silent: true });
        }
        this.daysCount = this.getDaysCount(newVal);
      }
    }, true);
  }

  /** Handles the external change of the disabled dates. */
  private onDisabledDatesExternalChange() {
    this.validationError = "";
    this.disableDates();

    if (this._value) {
      const date = Dates.asJSDate(this._value);

      if (this.isDateDisabled(date)) {
        this.validationError = "SALAXY.NG1.DatepickerComponent.validationErrorDisabled";
      }
    }
  }

  /**
   * Initialize the datepicker element.
   */
  public initDatepicker(isManualInputInit: boolean = false) {
    if (this.datepickerInitialized) return;

    const datepickerElem = this.$element[0].querySelector(".datepicker-input") as HTMLElement;
    if (!datepickerElem) {
      return;
    }
    this.datepickerInitialized = true;

    let viewLevel = "days";
    let minView = "days" as AirDatepickerViews;
    let multipleDates: boolean | number = false;
    let dateFormat = "dd.MM.yyyy";
    let minDate: Date | string | number;
    let maxDate: Date | string | number;
    const clearButton = {
      content: Translations.get("SALAXY.UI_Terms.clear"),
      className: "clear-button btn-danger",
      onClick: () => {
        this._manualInput = null;
        this.clearValues();
        this.validate();
        this.$scope.$evalAsync();
      }
    };

    let buttons: any[] = [];
    if (this.showButtons) {
      buttons.push(clearButton);
    }

    if (this.isMultiple) {
      multipleDates = 365;
      buttons = [];
    }

    if (this.format) {
      switch (this.format) {
        case "month":
          dateFormat = "MMMM, yyyy";
          viewLevel = "months";
          minView = "months";
          break;
        case "year":
          dateFormat = "yyyy";
          viewLevel = "years";
          minView = "years";
          break;
      }
    }

    if (this.dateDisabled) {
      switch (this.dateDisabled) {
        case "next-weekday":
          minDate = Dates.asJSDate(Dates.asDate("today"));
          break;
        case "salary":
          minDate = Dates.asJSDate(Dates.add(Dates.asDate("today"), -1, "year"));
      }
    } else {
      if (this.minDate) {
        minDate = this.minDate;
      }
      if (this.maxDate) {
        maxDate = this.maxDate;
      }
    }

    const localeMap = {
      fi: localeFi,
      en: localeEn,
      sv: localeSv
    };

    const createPopperPlacement = !this.isInline;
    const lang = Translations.getLanguage();

    this.datepicker = new AirDatepicker(datepickerElem, {
      locale: localeMap?.[lang] || localeFi,
      view: viewLevel as AirDatepickerViews,
      multipleDates: multipleDates,
      autoClose: true,
      buttons: buttons,
      inline: this.isInline,
      minView: minView,
      dateFormat: dateFormat,
      minDate: minDate,
      maxDate: maxDate,
      timepicker: this.timepicker,
      onSelect: (event) => {
        this.onSelect(event.date);
      },
      onChangeViewDate: () => {
        if (this.datepicker?.currentView == "days") {
          this.disableDates();
        }
      },
      // Fix for the popper issue that causes quick flickering when the datepicker is shown.
      onShow: () => {
        const el = this.datepicker.$datepicker;
        el.style.opacity = "0";

        setTimeout(() => {
          el.style.opacity = "1";
        }, 50);
      },
      position({ $datepicker, $target, $pointer }) {
        if (!createPopperPlacement) {
          return;
        }
        createPopper($target, $datepicker, {
          placement: "bottom-end",
          strategy: "fixed",
          modifiers: [
            {
              name: "computeStyles",
              options: { adaptive: false }
            },
            {
              name: "arrow",
              options: { element: $pointer }
            },
          ]
        });
      }
    });

    this.disableDates();

    if (this._value) {
      this.setDatepickerCalendarValue(this._value);
    }
    if (!isManualInputInit && !this.isInline) {
      this.datepicker.show();
    }
  }

  /** Disables dates in the datepicker. Used right after the initialization or when an external value dictates so. */
  private disableDates() {
    const viewDates = this.datepicker.getViewDates();
    this.datepicker.enableDate(viewDates);
    const disabledDates = viewDates.filter(date => this.isDateDisabled(date));
    this.datepicker.disableDate(disabledDates);
  }

  /**
   * Calculates the days count from a value object.
   * @param value Value object with days array or daysCount property.
   * @returns The number of days.
   */
  private getDaysCount(value: any): number {
    return value.days ? value.days.length : value.daysCount;
  }

  /** Wrapper for updating component value. Note: update is always made "silent", so that the onSelect event won't get fired. */
  private setDatepickerCalendarValue(value: any) {
    if (!this.isMultiple) {
      this.datepicker.selectDate(value, { silent: true });
      if (value) {
        this.datepicker.setViewDate(value);
      }
    } else {
      const dpValue = value.days ? value.days : [];
      this.daysCount = this.getDaysCount(value);
      const viewDate = dpValue[0] || null;

      this.datepicker.selectDate(dpValue, { silent: true });
      if (viewDate) {
        this.datepicker.setViewDate(viewDate);
      }
    }
  }

  /**
   * Pick date from the calendar plugin event method.
   * @param date Date value that's picked in the datepicker.
   */
  private onSelect(date) {
    // Date is either a string or an array of date strings (in multiple mode).
    let dateVal: string | string[];
    let viewValue: string | string[] | { days: string | string[]; daysCount: number };

    if (Array.isArray(date)) {
      dateVal = date.map((d) => Dates.asDate(d));
    } else if (date == null) {
      dateVal = null;
    } else if (this.timepicker) {
      dateVal = date.toISOString();
    } else {
      dateVal = Dates.asDate(date);
    }

    // For inline multiple datepickers, preserve the object structure (period with start/end/days).
    if (this.isInline && this.isMultiple) {
      viewValue = {
        ...this._value,
        days: dateVal,
        daysCount: Array.isArray(dateVal) ? dateVal.length : 0
      };
    } else {
      viewValue = dateVal;
    }

    this._value = viewValue;
    this._manualInput = null;
    this.model.$setViewValue(viewValue);
    this.validate();
  }

  /**
   * Determines if a given date is disabled based on the dateDisabled configuration.
   * Handles both custom disable functions and built-in disable methods.
   * @param date The date to evaluate.
   * @returns boolean Either true = date is disabled, false = not disabled.
   */
  private isDateDisabled(date: Date): boolean {
    if (this.dateDisabled && this.disableFunction) {
      return this.disableFunction(date);
    }
    return this.checkDateDisabled(date);
  }

  /**
   * Checks if given date is disabled with one of two methods: "next-weekday" and "salary".
   * @param date The date to evaluate.
   * @returns boolean Either true = date is disabled, false = not disabled.
   */
  public checkDateDisabled(date) {
    const role =  this.sessionService.isInRole("pro") ? "pro" :
              this.sessionService.isInRole("admin") ? "admin" : null;
    switch (this.dateDisabled) {
      case "next-weekday":
        return Dates.asDate(date) < Dates.addWorkdays("today", 1) || !Dates.isWorkday(date);
      case "salary":
        return !SalaryDateLogic.isValidSalaryDate(date, null, null, true, this.paymentChannel, this.zeroPayment, role);
    }
  }

  /** Gets the readonly value either from the controller or from the form */
  protected getReadOnly(): boolean {
    // This readOnly can be true, false or undefined
    const readOnlyValue = (this.readOnly + "").trim().toLowerCase();
    if (readOnlyValue === "true" || readOnlyValue === "1") {
      return true;
    }
    if (readOnlyValue === "false" || readOnlyValue === "0") {
      return false;
    }
    const readOnlyFormValue = !!this?.form?.$$element[0]?.attributes?.readonly;
    if (readOnlyFormValue) {
      return true;
    }

    return false;
  }

  /** Gets or sets the presence of the required validator in the underlying model. */
  public get require(): boolean {
    return !!this.model?.$validators.required;
  }
  public set require(value: boolean) {
    const trySetValidator = () => {
      if (!this.model) {
        setTimeout(trySetValidator);
        return;
      }
      const hasRequiredValidator = !!this.model.$validators.required;
      if (value && !hasRequiredValidator) {
        this.model.$validators.required = (modelValue, viewValue) => {
          return !this.model.$isEmpty(viewValue);
        };
      } else if (!value && hasRequiredValidator) {
        delete this.model.$validators.required;
      }
    };

    trySetValidator();
  }

  /** Do validations. */
  public validate() {
    if (this.require && !this.model.$viewValue) {
      this.validationError = "SALAXY.NG1.DatepickerComponent.validationErrorRequired";
      this.model.$setValidity("required", false);
      return false;
    }

    this.validationError = "";
    this.model.$setValidity("required", true);
    return true;
  }

  /**
   * Manually typed date changed.
   */
  public changeManualDate() {
    this.validationError = "";
    const typedValue = this._manualInput?.trim();

    if (!typedValue) {
      if (this.require) {
        return this.setError("SALAXY.NG1.DatepickerComponent.validationErrorRequired");
      }
      this.clearValues();
      return;
    }

    const normalized = this.normalizeManualInput(typedValue);
    const parsed = Dates.parseDate(normalized, "DD.MM.YYYY");

    if (!parsed) {
      return this.setError(this.getValidationErrorKey());
    }

    if (!this.format && this.isDateDisabled(Dates.asJSDate(parsed))) {
      return this.setError("SALAXY.NG1.DatepickerComponent.validationErrorDisabled");
    }

    this._value = parsed;
    this._manualInput = null;
    this.model.$setViewValue(parsed);
    this.setDatepickerCalendarValue(parsed);
  }

  /**
   * Normalizes manual input based on format type.
   */
  private normalizeManualInput(input: string): string {
    if (this.format == "month") {
      const parts = input.split(/[./-]/);
      return `01.${parts[0]?.padStart(2, "0")}.${parts[1]}`;
    }

    if (this.format == "year") {
      return `01.01.${input}`;
    }

    const parts = input.split(".");
    if (parts.length == 3) {
      return `${parts[0].padStart(2, "0")}.${parts[1].padStart(2, "0")}.${parts[2]}`;
    } else {
      return input;
    }
  }

  /**
   * Gets the appropriate validation error key based on format.
   */
  private getValidationErrorKey(): string {
    if (this.format == "month") return "SALAXY.NG1.DatepickerComponent.validationErrorMonth";
    if (this.format == "year") return "SALAXY.NG1.DatepickerComponent.validationErrorYear";
    return "SALAXY.NG1.DatepickerComponent.validationErrorDefault";
  }

  /**
   * Sets the error message of the datepicker.
   * @param errorMessage The error message to set.
   */
  private setError(errorMessage: string) {
    this.validationError = errorMessage;
    this.clearValues();
    return;
  }

  /*
   * Clears the values of the datepicker.
   */
  private clearValues() {
    this._value = null;
    this.model.$setViewValue(null);
    this.datepicker.clear({ silent: true });
  }
}